import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiMagnify, mdiPlus, mdiDelete, mdiDownload } from '@mdi/js';
import { ComponentType, ComponentTypeEnum } from '../../types';
import { apiService } from '../../services/apiService';
import { DataTable, Column } from '../components/DataTable';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import { adminTheme } from '../styles/adminTheme';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${adminTheme.colors.text};
`;

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${adminTheme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: ${adminTheme.colors.primaryHover};
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const TabBar = styled.div`
  display: flex;
  gap: 2px;
  background: #f1f3f4;
  border-radius: 6px;
  padding: 2px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 6px 12px;
  font-size: 12px;
  font-weight: ${p => p.$active ? 600 : 400};
  color: ${p => p.$active ? 'white' : adminTheme.colors.textSecondary};
  background: ${p => p.$active ? adminTheme.colors.primary : 'transparent'};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    ${p => !p.$active && `background: #e8eaed;`}
  }
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid ${adminTheme.colors.border};
  border-radius: 6px;
  background: white;
  flex: 1;
  max-width: 300px;

  input {
    border: none;
    outline: none;
    font-size: 13px;
    flex: 1;
    color: ${adminTheme.colors.text};
  }
`;

const ActionBtn = styled.button<{ $danger?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: ${p => p.$danger ? adminTheme.colors.danger : adminTheme.colors.textSecondary};

  &:hover {
    background: ${p => p.$danger ? '#fce8e6' : '#f1f3f4'};
  }
`;

const TypeIcon = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: #f1f3f4;
  color: ${adminTheme.colors.textSecondary};
  text-transform: capitalize;
`;

const allTypes: { key: string; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pump', label: 'Pump' },
  { key: 'pipe', label: 'Pipe' },
  { key: 'branch', label: 'Branch' },
  { key: 'merge', label: 'Merge' },
  { key: 'restriction', label: 'Restriction' },
  { key: 'heat_source', label: 'Heat Source' },
  { key: 'cooling_package', label: 'Cooling Pkg' },
];

export const ComponentsListPage: React.FC = () => {
  const [components, setComponents] = useState<ComponentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<ComponentType | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const loadComponents = () => {
    setLoading(true);
    apiService.getComponents()
      .then(setComponents)
      .catch(() => toast('Failed to load components', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadComponents(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    let list = components;
    if (typeFilter !== 'all') {
      list = list.filter(c => c.component_type === typeFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.name.toLowerCase().includes(q) ||
        (c.manufacturer || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [components, typeFilter, search]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiService.deleteComponent(deleteTarget.id);
      toast('Component deleted', 'success');
      setDeleteTarget(null);
      loadComponents();
    } catch {
      toast('Failed to delete component', 'error');
    }
  };

  const handleDownloadJson = (comp: ComponentType, e: React.MouseEvent) => {
    e.stopPropagation();
    const blob = new Blob([JSON.stringify(comp, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${comp.name.replace(/\s+/g, '_')}.json`;
    a.click();
  };

  const columns: Column<ComponentType>[] = [
    { key: 'name', header: 'Name', sortable: true },
    {
      key: 'component_type',
      header: 'Type',
      sortable: true,
      render: (row) => <TypeIcon>{row.component_type_display}</TypeIcon>,
    },
    { key: 'manufacturer', header: 'Manufacturer', sortable: true },
    {
      key: 'curves',
      header: 'Curves',
      render: (row) => <span>{row.curves?.length ?? 0} curves</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '100px',
      render: (row) => (
        <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
          <ActionBtn title="Download JSON" onClick={(e) => handleDownloadJson(row, e)}>
            <Icon path={mdiDownload} size={0.65} />
          </ActionBtn>
          <ActionBtn $danger title="Delete" onClick={() => setDeleteTarget(row)}>
            <Icon path={mdiDelete} size={0.65} />
          </ActionBtn>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader>
        <PageTitle>Components</PageTitle>
        <AddBtn onClick={() => navigate('/panel/components/new')}>
          <Icon path={mdiPlus} size={0.65} />
          Add Component
        </AddBtn>
      </PageHeader>

      <Toolbar>
        <TabBar>
          {allTypes.map(t => (
            <Tab key={t.key} $active={typeFilter === t.key} onClick={() => setTypeFilter(t.key)}>
              {t.label}
            </Tab>
          ))}
        </TabBar>
        <SearchBox>
          <Icon path={mdiMagnify} size={0.65} color={adminTheme.colors.textMuted} />
          <input
            placeholder="Search name or manufacturer..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </SearchBox>
      </Toolbar>

      <DataTable
        columns={columns}
        data={filtered}
        loading={loading}
        onRowClick={(row) => navigate(`/panel/components/${row.id}`)}
        emptyMessage="No components found"
      />

      {deleteTarget && (
        <ConfirmDialog
          title="Delete Component"
          message={`Are you sure you want to delete "${deleteTarget.name}"? This action cannot be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};
